// AskPanel — collapsible AI Q&A sidebar panel.
// Desktop: 320px side panel anchored bottom-right. Mobile: bottom sheet.

import { useState, useRef, useEffect, useCallback } from "react";
import {
  useAsk,
  useSuggestions,
  fetchCitationByChunkId,
  type CitationDetails,
} from "../../hooks/useAI";
import { useLocation } from "react-router-dom";

// ── Page context mapping ──────────────────────────────────────────────────────

function pageFromPath(pathname: string): string {
  if (pathname === "/" || pathname === "") return "overview";
  if (pathname.startsWith("/forge")) return "forge";
  if (pathname.startsWith("/staffing") || pathname.startsWith("/ops/staffing"))
    return "staffing";
  if (pathname.startsWith("/retention")) return "retention";
  return "overview";
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function ChatIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function SendIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function XIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function SpinnerIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="animate-spin"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}

// ── Confidence badge ──────────────────────────────────────────────────────────

function ConfidenceBadge({ level }: { level: string }) {
  const normalized = level.toLowerCase();
  const config: Record<string, { label: string; cls: string }> = {
    high: {
      label: "High Confidence",
      cls: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
    },
    medium: {
      label: "Medium Confidence",
      cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
    },
    low: {
      label: "Low Confidence",
      cls: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    },
  };
  const { label, cls } = config[normalized] ?? config["medium"];
  return (
    <span
      className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}
    >
      {label}
    </span>
  );
}

// ── Typing indicator ──────────────────────────────────────────────────────────

function TypingIndicator() {
  return (
    <div className="flex items-center gap-1 py-2">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-1.5 h-1.5 rounded-full bg-brand-400 opacity-75"
          style={{ animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }}
        />
      ))}
    </div>
  );
}

// ── Model badge ───────────────────────────────────────────────────────────────

function ModelBadge({ model }: { model: string }) {
  // Reflect the model that actually answered. The Ask pipeline runs a local
  // Ollama-compatible endpoint (gpt-oss / llama) — don't default to "Claude".
  const m = model.toLowerCase();
  const label = m.includes("gpt-oss")
    ? "gpt-oss"
    : m.includes("llama")
      ? "Llama"
      : m.includes("haiku")
        ? "Haiku"
        : m.includes("sonnet")
          ? "Sonnet"
          : m.includes("claude")
            ? "Claude"
            : model || "model";
  return (
    <span className="inline-block text-xs font-mono px-1.5 py-0.5 rounded bg-brand-500/20 text-brand-300">
      {label}
    </span>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AskPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const location = useLocation();
  const page = pageFromPath(location.pathname);

  const { data: suggestionsData } = useSuggestions(page);
  const {
    isStreaming,
    answer,
    confidence,
    model,
    error,
    rateLimitRemaining,
    ask,
    reset,
    queryId,
    sessionId,
    citations,
    sendFeedback,
  } = useAsk();
  const [sessionIdRef, setSessionIdRef] = useState<string>("");
  const [feedbackSent, setFeedbackSent] = useState<-1 | 0 | 1 | null>(null);
  const [openCitation, setOpenCitation] = useState<CitationDetails | null>(
    null,
  );

  useEffect(() => {
    if (sessionId && sessionId !== sessionIdRef) setSessionIdRef(sessionId);
  }, [sessionId, sessionIdRef]);

  useEffect(() => {
    if (!isStreaming) return;
    setFeedbackSent(null);
    setOpenCitation(null);
  }, [isStreaming]);

  const handleFeedback = useCallback(
    async (rating: -1 | 1) => {
      if (!queryId || feedbackSent !== null) return;
      setFeedbackSent(rating);
      await sendFeedback(queryId, rating);
    },
    [queryId, feedbackSent, sendFeedback],
  );

  const handleOpenCitation = useCallback(
    async (docIdPrefix: string, chunkIndex: number) => {
      // The server accepts full chunk_id; our in-message citations are a doc_id prefix + index
      // so we must resolve to a chunk_id. Use the search API restricted to this doc.
      const r = await fetch(
        `/api/memory/search?q=${encodeURIComponent(docIdPrefix)}`,
        { credentials: "same-origin" },
      ).catch(() => null);
      if (!r || !r.ok) return;
      const body = (await r.json()) as {
        results: Array<{
          chunk_id: string;
          doc_id: string;
          chunk_index: number;
        }>;
      };
      const match = body.results.find(
        (h) => h.doc_id === docIdPrefix && h.chunk_index === chunkIndex,
      );
      if (!match) return;
      const full = await fetchCitationByChunkId(match.chunk_id);
      if (full) setOpenCitation(full);
    },
    [],
  );

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const answerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll answer area on new tokens
  useEffect(() => {
    if (answerRef.current && isStreaming) {
      answerRef.current.scrollTop = answerRef.current.scrollHeight;
    }
  }, [answer, isStreaming]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSubmit = useCallback(async () => {
    const q = question.trim();
    if (!q || isStreaming) return;
    setQuestion("");
    await ask(q, page, sessionIdRef || undefined);
  }, [question, isStreaming, ask, page, sessionIdRef]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  const handleSuggestion = useCallback((q: string) => {
    setQuestion(q);
    inputRef.current?.focus();
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    reset();
    setQuestion("");
  }, [reset]);

  const suggestions = suggestionsData?.questions ?? [];
  const charCount = question.length;
  const atLimit = charCount >= 500;

  return (
    <>
      {/* Toggle button — bottom-right FAB */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-full shadow-lg bg-brand-500 hover:bg-brand-400 text-white font-medium text-sm transition-all hover:scale-105 active:scale-95"
          aria-label="Ask Ember about your DCVFD data"
        >
          <ChatIcon size={18} />
          <span className="hidden sm:inline">Ask Ember</span>
        </button>
      )}

      {/* Panel */}
      {isOpen && (
        <>
          {/* Mobile backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/40 sm:hidden"
            onClick={handleClose}
          />

          {/* Panel container */}
          <div
            className={[
              "fixed z-50 flex flex-col",
              // Mobile: bottom sheet
              "bottom-0 left-0 right-0 max-h-[85dvh] rounded-t-2xl sm:rounded-none",
              // Desktop: side panel
              "sm:bottom-6 sm:right-6 sm:left-auto sm:w-80 sm:max-h-[600px] sm:rounded-2xl",
              "shadow-2xl",
            ].join(" ")}
            style={{
              background: "var(--surface-raised)",
              border: "1px solid var(--border-default)",
            }}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0"
              style={{ borderColor: "var(--border-subtle)" }}
            >
              <div className="flex items-center gap-2">
                <ChatIcon size={16} />
                <span
                  className="font-semibold text-sm"
                  style={{ color: "var(--text-primary)" }}
                >
                  Ask Ember
                </span>
                {model && <ModelBadge model={model} />}
              </div>
              <button
                onClick={handleClose}
                className="p-1 rounded-md hover:bg-surface-overlay transition-colors"
                style={{ color: "var(--text-muted)" }}
                aria-label="Close"
              >
                <XIcon size={16} />
              </button>
            </div>

            {/* Answer / empty state area */}
            <div
              ref={answerRef}
              className="flex-1 overflow-y-auto px-4 py-3 min-h-[80px]"
              style={{ color: "var(--text-primary)" }}
            >
              {!answer && !isStreaming && !error && (
                <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                  Ask Ember any question about DCVFD membership, recruitment,
                  retention, or staffing metrics.
                </p>
              )}

              {(answer || isStreaming) && (
                <div className="space-y-3">
                  <p
                    className="text-sm leading-relaxed whitespace-pre-wrap"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {answer}
                    {isStreaming && !answer && <TypingIndicator />}
                  </p>

                  {!isStreaming && citations.length > 0 && (
                    <div className="pt-1 flex flex-wrap gap-1.5">
                      {citations.map((c) => (
                        <button
                          key={`${c.docIdPrefix}-${c.chunkIndex}`}
                          type="button"
                          onClick={() =>
                            handleOpenCitation(c.docIdPrefix, c.chunkIndex)
                          }
                          className="text-[11px] font-mono px-2 py-0.5 rounded-full border border-theme-subtle hover:bg-theme-overlay text-theme-muted"
                          title="View source passage"
                        >
                          [{c.docIdPrefix}#{c.chunkIndex}]
                        </button>
                      ))}
                    </div>
                  )}

                  {!isStreaming && confidence && (
                    <div className="pt-1 flex items-center justify-between gap-2">
                      <ConfidenceBadge level={confidence} />
                      {queryId && (
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            disabled={feedbackSent !== null}
                            onClick={() => handleFeedback(1)}
                            className={`text-sm px-1.5 py-0.5 rounded-md ${
                              feedbackSent === 1
                                ? "bg-green-500/20 text-green-300"
                                : "text-theme-muted hover:text-theme-primary"
                            } disabled:opacity-40`}
                            title="Helpful"
                          >
                            👍
                          </button>
                          <button
                            type="button"
                            disabled={feedbackSent !== null}
                            onClick={() => handleFeedback(-1)}
                            className={`text-sm px-1.5 py-0.5 rounded-md ${
                              feedbackSent === -1
                                ? "bg-red-500/20 text-red-300"
                                : "text-theme-muted hover:text-theme-primary"
                            } disabled:opacity-40`}
                            title="Not helpful"
                          >
                            👎
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {openCitation && (
                <div className="mt-4 rounded-lg border border-theme-subtle p-3 bg-theme-overlay/40">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-mono text-theme-muted">
                      [{openCitation.doc_id.slice(0, 8)}#
                      {openCitation.chunk_index}] {openCitation.kind}
                      {openCitation.source_date &&
                        ` — ${openCitation.source_date}`}
                    </span>
                    <button
                      type="button"
                      onClick={() => setOpenCitation(null)}
                      className="text-theme-muted hover:text-theme-primary text-xs"
                    >
                      close
                    </button>
                  </div>
                  <p className="text-sm font-medium text-theme-primary mb-1">
                    {openCitation.title}
                  </p>
                  {openCitation.section_path && (
                    <p className="text-xs text-theme-muted mb-2">
                      {openCitation.section_path}
                    </p>
                  )}
                  <p className="text-xs text-theme-secondary leading-relaxed whitespace-pre-wrap">
                    {openCitation.text}
                  </p>
                </div>
              )}

              {error && (
                <div
                  className="rounded-lg p-3 text-sm"
                  style={{
                    background: "rgba(220,38,38,0.08)",
                    color: "#dc2626",
                    border: "1px solid rgba(220,38,38,0.2)",
                  }}
                >
                  {error}
                </div>
              )}
            </div>

            {/* Suggested questions */}
            {!answer && !isStreaming && suggestions.length > 0 && (
              <div
                className="px-4 pb-2 flex flex-col gap-1.5 flex-shrink-0"
                style={{
                  borderTop: "1px solid var(--border-subtle)",
                  paddingTop: "0.75rem",
                }}
              >
                <p
                  className="text-xs font-medium uppercase tracking-wider"
                  style={{ color: "var(--text-muted)" }}
                >
                  Suggested
                </p>
                {suggestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => handleSuggestion(q)}
                    className="text-left text-xs px-3 py-2 rounded-lg transition-colors hover:opacity-80"
                    style={{
                      background: "var(--surface-overlay)",
                      color: "var(--text-secondary)",
                      border: "1px solid var(--border-subtle)",
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* Input area */}
            <div
              className="px-4 py-3 flex-shrink-0"
              style={{ borderTop: "1px solid var(--border-subtle)" }}
            >
              {/* Rate limit counter */}
              <div className="flex justify-between items-center mb-2">
                <span
                  className="text-xs"
                  style={{ color: "var(--text-muted)" }}
                >
                  {rateLimitRemaining} of 5 questions remaining
                </span>
                {answer && !isStreaming && (
                  <button
                    onClick={reset}
                    className="text-xs hover:underline"
                    style={{ color: "var(--text-muted)" }}
                  >
                    Clear
                  </button>
                )}
              </div>

              <div className="flex gap-2 items-end">
                <div className="flex-1 relative">
                  <textarea
                    ref={inputRef}
                    value={question}
                    onChange={(e) => setQuestion(e.target.value.slice(0, 500))}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask Ember anything…"
                    rows={2}
                    disabled={isStreaming || rateLimitRemaining === 0}
                    className="w-full resize-none rounded-lg px-3 py-2 text-sm outline-none transition-colors disabled:opacity-50"
                    style={{
                      background: "var(--surface-overlay)",
                      color: "var(--text-primary)",
                      border: "1px solid var(--border-default)",
                    }}
                  />
                  <span
                    className="absolute bottom-2 right-2 text-xs"
                    style={{ color: atLimit ? "#dc2626" : "var(--text-muted)" }}
                  >
                    {charCount}/500
                  </span>
                </div>
                <button
                  onClick={handleSubmit}
                  disabled={
                    !question.trim() || isStreaming || rateLimitRemaining === 0
                  }
                  className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-lg bg-brand-500 hover:bg-brand-400 text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Send"
                >
                  {isStreaming ? (
                    <SpinnerIcon size={16} />
                  ) : (
                    <SendIcon size={16} />
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
